import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  Users,
  UserCheck,
  FileText,
  ShoppingBag,
  IndianRupee,
  TrendingUp,
  Timer,
  DoorOpen,
} from "lucide-react";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { RefreshBar } from "@/components/admin/RefreshBar";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { toneClass, type OpsTone } from "@/lib/admin/order-badges";
import { getWebsiteAnalytics } from "@/lib/admin/website-analytics.functions";
import type {
  AnalyticsRangeKey,
  NamedMetric,
  WebsiteAnalytics,
} from "@/lib/admin/website-analytics.types";

export const Route = createFileRoute("/_admin/admin/website-analytics")({
  component: WebsiteAnalyticsPage,
});

const RANGES: Array<{ key: AnalyticsRangeKey; label: string }> = [
  { key: "today", label: "Today" },
  { key: "yesterday", label: "Yesterday" },
  { key: "7d", label: "Last 7 days" },
  { key: "30d", label: "Last 30 days" },
  { key: "month", label: "This month" },
  { key: "custom", label: "Custom" },
];

const TONE_VAR: Record<OpsTone, string> = {
  neutral: "var(--ops-neutral)",
  info: "var(--ops-info)",
  warn: "var(--ops-warn)",
  teal: "var(--ops-teal)",
  purple: "var(--ops-purple)",
  success: "var(--ops-success)",
  danger: "var(--ops-danger)",
};

const PIE_TONES: OpsTone[] = [
  "info",
  "success",
  "purple",
  "warn",
  "teal",
  "danger",
  "neutral",
  "info",
];

function inr(n: number): string {
  return `₹${Math.round(n).toLocaleString("en-IN")}`;
}
function pct(n: number): string {
  return `${(n * 100).toFixed(2)}%`;
}
function duration(sec: number): string {
  const m = Math.floor(sec / 60);
  const s = Math.round(sec % 60);
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

function KpiCard({
  label,
  value,
  tone,
  icon: Icon,
  delta,
  loading,
  to,
  hint,
}: {
  label: string;
  value: string;
  tone: OpsTone;
  icon: typeof Users;
  delta?: number;
  loading: boolean;
  to?: string;
  hint?: string;
}) {
  const body = (
    <div
      className={`h-full rounded-lg border p-3 transition-shadow ${toneClass(tone)} ${
        to ? "hover:shadow-md" : ""
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <p className="truncate text-[11px] font-medium uppercase tracking-wide opacity-80">
          {label}
        </p>
        <Icon className="h-4 w-4 shrink-0 opacity-70" aria-hidden />
      </div>
      {loading ? (
        <Skeleton className="mt-2 h-7 w-20" />
      ) : (
        <p className="mt-1 truncate text-2xl font-semibold tabular-nums">{value}</p>
      )}
      <p className="mt-1 text-[11px] opacity-75">
        {delta !== undefined && !loading
          ? `${delta >= 0 ? "▲" : "▼"} ${Math.abs(delta * 100).toFixed(1)}% vs previous`
          : (hint ?? "")}
      </p>
    </div>
  );
  return to ? (
    <Link to={to} className="block h-full focus-visible:outline-none">
      {body}
    </Link>
  ) : (
    body
  );
}

function Panel({
  title,
  subtitle,
  children,
  className = "",
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <section className={`rounded-lg border border-border bg-card p-4 ${className}`}>
      <header className="mb-3 flex items-baseline justify-between gap-2">
        <h2 className="text-sm font-semibold text-foreground">{title}</h2>
        {subtitle ? (
          <span className="text-[11px] text-muted-foreground">{subtitle}</span>
        ) : null}
      </header>
      {children}
    </section>
  );
}

function tooltipStyle() {
  return {
    contentStyle: {
      background: "var(--color-card)",
      border: "1px solid var(--color-border)",
      borderRadius: "0.5rem",
      fontSize: "12px",
      color: "var(--color-foreground)",
    },
    labelStyle: { color: "var(--color-muted-foreground)" },
  } as const;
}

function MetricBars({ rows, tone }: { rows: NamedMetric[]; tone: OpsTone }) {
  const total = rows.reduce((s, r) => s + r.value, 0) || 1;
  return (
    <ul className="space-y-2">
      {rows.map((r) => {
        const p = Math.round((r.value / total) * 100);
        return (
          <li key={r.name} className="text-sm">
            <div className="flex items-center justify-between gap-2">
              <span className="truncate text-foreground">{r.name}</span>
              <span className="tabular-nums text-muted-foreground">
                {r.value.toLocaleString("en-IN")} · {p}%
              </span>
            </div>
            <div className="mt-1 h-1.5 rounded bg-muted">
              <div
                className="h-1.5 rounded"
                style={{ width: `${p}%`, background: TONE_VAR[tone] }}
              />
            </div>
          </li>
        );
      })}
    </ul>
  );
}

function WebsiteAnalyticsPage() {
  const [range, setRange] = useState<AnalyticsRangeKey>("7d");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const fetchAnalytics = useServerFn(getWebsiteAnalytics);

  const q = useQuery({
    queryKey: ["admin", "website-analytics", range, from, to],
    queryFn: () =>
      fetchAnalytics({
        data: { range, ...(range === "custom" ? { from, to } : {}) },
      }),
    staleTime: 30_000,
    placeholderData: (prev) => prev,
  });

  const d = q.data as WebsiteAnalytics | undefined;
  const loading = q.isLoading;
  const k = d?.kpis;

  const funnelRows = useMemo(() => {
    const rows = d?.funnel ?? [];
    const top = rows[0]?.value ?? 0;
    return rows.map((r, i) => ({
      ...r,
      ofEntry: top > 0 ? r.value / top : 0,
      step: i > 0 && rows[i - 1]!.value > 0 ? r.value / rows[i - 1]!.value : 1,
    }));
  }, [d]);

  return (
    <div>
      <AdminPageHeader
        title="Website Analytics"
        description="Traffic, engagement, funnel and marketing performance for the public website."
      />

      <RefreshBar
        targets={[{ key: "website-analytics", label: "analytics" }]}
        busy={q.isFetching}
        lastUpdated={q.dataUpdatedAt}
      />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        {RANGES.map((r) => (
          <Button
            key={r.key}
            size="sm"
            variant={range === r.key ? "default" : "outline"}
            onClick={() => setRange(r.key)}
          >
            {r.label}
          </Button>
        ))}
        {range === "custom" ? (
          <div className="flex items-center gap-2">
            <Input
              type="date"
              aria-label="From date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              className="h-9 w-40"
            />
            <span className="text-xs text-muted-foreground">to</span>
            <Input
              type="date"
              aria-label="To date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className="h-9 w-40"
            />
          </div>
        ) : null}
        <span className="ml-auto text-xs text-muted-foreground">
          {d ? (
            d.source === 'posthog' 
              ? `Source: PostHog${(d.kpis.visitors === 0 && d.kpis.pageViews === 0) ? ' — No data' : ''}` 
              : `${d.rangeLabel} · Source: Demo`
          ) : "Loading…"}
        </span>
      </div>

      {/* KPI cards */}
      <section aria-label="Key metrics" className="mb-6">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 xl:grid-cols-8">
          <KpiCard
            label="Visitors"
            value={(k?.visitors ?? 0).toLocaleString("en-IN")}
            tone="info"
            icon={Users}
            delta={k?.deltas.visitors}
            loading={loading}
          />
          <KpiCard
            label="Unique visitors"
            value={(k?.uniqueVisitors ?? 0).toLocaleString("en-IN")}
            tone="teal"
            icon={UserCheck}
            delta={k?.deltas.uniqueVisitors}
            loading={loading}
          />
          <KpiCard
            label="Page views"
            value={(k?.pageViews ?? 0).toLocaleString("en-IN")}
            tone="purple"
            icon={FileText}
            delta={k?.deltas.pageViews}
            loading={loading}
          />
          <KpiCard
            label="Orders placed"
            value="Not measured"
            tone="success"
            icon={ShoppingBag}
            delta={k?.deltas.orders}
            loading={loading}
            to="/admin/orders"
          />
          <KpiCard
            label="Revenue"
            value="Not measured"
            tone="success"
            icon={IndianRupee}
            delta={k?.deltas.revenue}
            loading={loading}
            to="/admin/reports"
          />
          <KpiCard
            label="Conversion rate"
            value="Not measured"
            tone="warn"
            icon={TrendingUp}
            delta={k?.deltas.conversion}
            loading={loading}
            to="/admin/operations"
          />
          <KpiCard
            label="Avg session"
            value={duration(k?.avgSessionSec ?? 0)}
            tone="neutral"
            icon={Timer}
            delta={k?.deltas.avgSessionSec}
            loading={loading}
          />
          <KpiCard
            label="Bounce rate"
            value={`${Math.round((k?.bounceRate ?? 0) * 100)}%`}
            tone="danger"
            icon={DoorOpen}
            delta={k?.deltas.bounceRate}
            loading={loading}
          />
        </div>
      </section>

      {/* Traffic overview */}
      <div className="mb-6 grid grid-cols-1 gap-4 xl:grid-cols-3">
        <Panel title="Traffic overview" subtitle="Visitors & sessions" className="xl:col-span-2">
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={d?.timeseries ?? []}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} stroke="var(--color-muted-foreground)" />
                <YAxis tick={{ fontSize: 11 }} stroke="var(--color-muted-foreground)" />
                <Tooltip {...tooltipStyle()} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Line
                  type="monotone"
                  dataKey="visitors"
                  name="Visitors"
                  stroke={TONE_VAR.info}
                  strokeWidth={2}
                  dot={false}
                />
                <Line
                  type="monotone"
                  dataKey="sessions"
                  name="Sessions"
                  stroke={TONE_VAR.purple}
                  strokeWidth={2}
                  dot={false}
                />
                <Line
                  type="monotone"
                  dataKey="orders"
                  name="Orders"
                  stroke={TONE_VAR.success}
                  strokeWidth={2}
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Panel>

        <Panel title="Revenue trend" subtitle="Rupees per day">
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={d?.timeseries ?? []}>
                <defs>
                  <linearGradient id="revFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={TONE_VAR.teal} stopOpacity={0.45} />
                    <stop offset="100%" stopColor={TONE_VAR.teal} stopOpacity={0.03} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} stroke="var(--color-muted-foreground)" />
                <YAxis tick={{ fontSize: 11 }} stroke="var(--color-muted-foreground)" />
                <Tooltip {...tooltipStyle()} formatter={(v: number) => inr(v)} />
                <Area
                  type="monotone"
                  dataKey="revenue"
                  name="Revenue"
                  stroke={TONE_VAR.teal}
                  strokeWidth={2}
                  fill="url(#revFill)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Panel>
      </div>

      {/* Most visited pages */}
      <Panel title="Most visited pages" subtitle="Ranked by page views" className="mb-6">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[820px] text-sm">
            <thead>
              <tr className="border-b border-border text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                <th className="py-2 pr-3 font-medium">Page</th>
                <th className="py-2 pr-3 text-right font-medium">Views</th>
                <th className="py-2 pr-3 text-right font-medium">Unique visitors</th>
                <th className="py-2 pr-3 text-right font-medium">Avg time</th>
                <th className="py-2 pr-3 text-right font-medium">Bounce</th>
                <th className="py-2 pr-3 text-right font-medium">Exit</th>
                <th className="py-2 text-right font-medium">Conversion</th>
              </tr>
            </thead>
            <tbody>
              {loading
                ? Array.from({ length: 6 }).map((_, i) => (
                    <tr key={i}>
                      <td colSpan={7} className="py-2">
                        <Skeleton className="h-5 w-full" />
                      </td>
                    </tr>
                  ))
                : (d?.pages ?? []).map((p) => (
                    <tr key={p.path} className="border-b border-border/60">
                      <td className="py-2 pr-3">
                        <span className="font-medium text-foreground">{p.label}</span>{" "}
                        <span className="text-xs text-muted-foreground">{p.path}</span>
                      </td>
                      <td className="py-2 pr-3 text-right tabular-nums">
                        {p.views.toLocaleString("en-IN")}
                      </td>
                      <td className="py-2 pr-3 text-right tabular-nums">
                        {p.visitors.toLocaleString("en-IN")}
                      </td>
                      <td className="py-2 pr-3 text-right tabular-nums">
                        {duration(p.avgTimeSec)}
                      </td>
                      <td className="py-2 pr-3 text-right tabular-nums">
                        {Math.round(p.bounceRate * 100)}%
                      </td>
                      <td className="py-2 pr-3 text-right tabular-nums">
                        {Math.round(p.exitRate * 100)}%
                      </td>
                      <td className="py-2 text-right tabular-nums">{pct(p.conversion)}</td>
                    </tr>
                  ))}
            </tbody>
          </table>
        </div>
      </Panel>

      {/* Page performance */}
      <Panel title="Page performance" subtitle="Business value per page" className="mb-6">
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={d?.pages ?? []}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 10 }}
                interval={0}
                angle={-25}
                textAnchor="end"
                height={70}
                stroke="var(--color-muted-foreground)"
              />
              <YAxis tick={{ fontSize: 11 }} stroke="var(--color-muted-foreground)" />
              <Tooltip {...tooltipStyle()} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="views" name="Views" fill={TONE_VAR.info} radius={[3, 3, 0, 0]} />
              <Bar dataKey="visitors" name="Visitors" fill={TONE_VAR.purple} radius={[3, 3, 0, 0]} />
              <Bar dataKey="orders" name="Orders" fill={TONE_VAR.success} radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {(d?.pages ?? []).map((p) => (
            <div key={p.path} className="rounded-lg border border-border p-3">
              <p className="truncate text-sm font-medium text-foreground">{p.label}</p>
              <dl className="mt-2 grid grid-cols-2 gap-1 text-xs text-muted-foreground">
                <dt>Views</dt>
                <dd className="text-right tabular-nums text-foreground">
                  {p.views.toLocaleString("en-IN")}
                </dd>
                <dt>Visitors</dt>
                <dd className="text-right tabular-nums text-foreground">
                  {p.visitors.toLocaleString("en-IN")}
                </dd>
                <dt>Avg time</dt>
                <dd className="text-right tabular-nums text-foreground">
                  {duration(p.avgTimeSec)}
                </dd>
                <dt>Orders</dt>
                <dd className="text-right tabular-nums text-foreground">{p.orders}</dd>
                <dt>Revenue</dt>
                <dd className="text-right tabular-nums text-foreground">{inr(p.revenue)}</dd>
              </dl>
            </div>
          ))}
        </div>
      </Panel>

      {/* Audience */}
      <div className="mb-6 grid grid-cols-1 gap-4 lg:grid-cols-2 xl:grid-cols-3">
        <Panel title="Traffic sources" subtitle="Sessions by channel">
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={d?.sources ?? []}
                  dataKey="value"
                  nameKey="name"
                  innerRadius={50}
                  outerRadius={85}
                  paddingAngle={2}
                >
                  {(d?.sources ?? []).map((s, i) => (
                    <Cell key={s.name} fill={TONE_VAR[PIE_TONES[i % PIE_TONES.length]!]} />
                  ))}
                </Pie>
                <Tooltip {...tooltipStyle()} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </Panel>

        <Panel title="Devices" subtitle="Visitors by device type">
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={d?.devices ?? []}
                  dataKey="value"
                  nameKey="name"
                  innerRadius={50}
                  outerRadius={85}
                  paddingAngle={2}
                >
                  {(d?.devices ?? []).map((s, i) => (
                    <Cell key={s.name} fill={TONE_VAR[PIE_TONES[i % PIE_TONES.length]!]} />
                  ))}
                </Pie>
                <Tooltip {...tooltipStyle()} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </Panel>

        <Panel title="Browsers">
          <MetricBars rows={d?.browsers ?? []} tone="info" />
        </Panel>

        <Panel title="Operating systems">
          <MetricBars rows={d?.operatingSystems ?? []} tone="purple" />
        </Panel>

        <Panel title="Cities" subtitle="Mumbai neighbourhoods">
          <MetricBars rows={d?.cities ?? []} tone="teal" />
        </Panel>

        <Panel title="Marketing performance" subtitle="Lead-generating actions">
          <MetricBars rows={d?.marketing ?? []} tone="success" />
        </Panel>
      </div>

      {/* Funnel */}
      <Panel
        title="Customer journey funnel"
        subtitle="Unique visitors reaching each step"
        className="mb-6"
      >
        <ol className="space-y-3">
          {funnelRows.map((r, i) => (
            <li key={r.name}>
              <div className="flex items-center justify-between gap-2 text-sm">
                <span className="font-medium text-foreground">
                  {i + 1}. {r.name}
                </span>
                <span className="tabular-nums text-muted-foreground">
                  {r.value.toLocaleString("en-IN")} · {Math.round(r.ofEntry * 100)}% of entry
                  {i > 0 ? ` · step ${Math.round(r.step * 100)}%` : ""}
                </span>
              </div>
              <div className="mt-1 h-2.5 rounded bg-muted">
                <div
                  className="h-2.5 rounded"
                  style={{
                    width: `${Math.round(r.ofEntry * 100)}%`,
                    background: TONE_VAR[PIE_TONES[i % PIE_TONES.length]!],
                  }}
                />
              </div>
            </li>
          ))}
        </ol>
      </Panel>

      {/* Heat metrics */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 xl:grid-cols-3">
        <Panel title="Top clicked buttons">
          <MetricBars rows={d?.heat.buttons ?? []} tone="warn" />
        </Panel>
        <Panel title="Top searched dishes">
          <MetricBars rows={d?.heat.dishes ?? []} tone="success" />
        </Panel>
        <Panel title="Top searched plans">
          <MetricBars rows={d?.heat.plans ?? []} tone="purple" />
        </Panel>
        <Panel title="Top searched neighbourhoods">
          <MetricBars rows={d?.heat.neighbourhoods ?? []} tone="teal" />
        </Panel>
        <Panel title="WhatsApp CTA clicks">
          <MetricBars rows={d?.heat.whatsapp ?? []} tone="info" />
        </Panel>
        <Panel title="Data source" subtitle="Future ready">
          <p className="text-sm text-muted-foreground">
            This dashboard reads a provider-agnostic feed. Connecting Google Analytics 4,
            PostHog, Meta Pixel, Google Ads or Microsoft Clarity only replaces the data
            provider — the layout, cards and charts stay exactly as they are.
          </p>
          <p className="mt-2 text-xs text-muted-foreground">
            Current source: <span className="font-medium text-foreground">{d?.source ?? "—"}</span>
          </p>
        </Panel>
      </div>
    </div>
  );
}
